import Dropdown from '@/components/forecast/Dropdown';
import { OperationSettlementBatchButton } from '@/components/operation-settlement/OperationSettlementBatchButton';
import { OperationSettlementButtonModal } from '@/components/operation-settlement/OperationSettlementButtonModal';
import { OperationSettlementStatusBar } from '@/components/operation-settlement/OperationSettlementStatusBar';
import { OperationSettlementSummary } from '@/components/operation-settlement/OperationSettlementSummary';
import { OperationSettlementSummaryModal } from '@/components/operation-settlement/OperationSettlementSummaryModal';
import { OperationSettlementTable } from '@/components/operation-settlement/OperationSettlementTable';
import { TopNavigationBar } from '@/components/common/TopNavigationBar';
import SideLayout from '@/layouts/SideLayout';
import { testSettlementTableData } from '@/utils/data';
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Divider } from '@/components/common/Divider';
import { SearchButton } from '@/components/common/SearchButton';

interface OperationSettlementPageProps {
  testId?: string;
  className?: string;
}

export const OperationSettlementPage: React.FC<
  OperationSettlementPageProps
> = ({ testId, className }) => {
  const { t } = useTranslation();
  const years = useMemo(
    () =>
      Array.from(
        { length: 26 },
        (_, i) => (2000 + i).toString() + t('forecast.year')
      ).sort((a, b) => {
        const yearA = parseInt(a, 10); // Extract the year number from the string
        const yearB = parseInt(b, 10);
        return yearB - yearA; // Compare in descending order
      }),
    []
  );
  const months = useMemo(
    () =>
      Array.from(
        { length: 12 },
        (_, i) => String(i + 1).padStart(2, '0') + t('forecast.month')
      ),
    []
  );
  const [selectedNavigationIndex] = useState(0);
  const [selectedYear] = useState(years[0]);
  const [selectedMonth] = useState(months[0]);
  const [showButtonModal, setShowButtonModal] = useState({
    isActive: false,
    title: '',
    description: '',
  });
  const [showSummaryModal, setShowSummaryModal] = useState({
    isActive: false,
    title: '',
  });
  const [operationSettlementPageRef, setOperationSettlementPageRef] =
    useState<HTMLDivElement | null>(null);

  return (
    <SideLayout ref={setOperationSettlementPageRef}>
      <div data-testid={testId} className={`${className}`}>
        <TopNavigationBar
          testId="operation-settlement-page-top-navigation"
          selectedIndex={selectedNavigationIndex}
        />
        <Divider className="bg-light-4" />
      </div>
      <section className="px-[29px] py-[27px]">
        <p
          data-testid="operation-settlement-page-prediction-settlement-title"
          className="noto-15-medium"
        >
          {t('settlement.navigation.predictionSettlement', {
            context: 'operation',
          })}
        </p>
        {/* Datepicker */}
        <div className="flex">
          {/* 계약 대상 구분 */}
          <div>
            <p
              data-testid="operation-settlement-page-prediction-settlement-contractee-type-title"
              className="noto-11-bold mt-[17px] leading-[22px] text-[#7A828A]"
            >
              {t('settlement.contracteeType')}
            </p>
            <Dropdown
              testId="operation-settlement-page-prediction-settlement-contractee-type-dropdown"
              items={['전체']}
              className="mt-[2px] w-[120px] [&>div:first-child>button]:noto-13-medium [&>div:last-child>div>p]:pop-12-normal [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[36px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:px-[10px] [&>div:first-child>button]:pb-[7px] [&>div:first-child>button]:pt-[6px] [&>div:first-child>button]:leading-[23px] [&>div:first-child>button]:ring-1 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-light-6 [&>div:last-child>div>p]:px-[10px] [&>div:last-child>div]:py-[13px] [&[data-headlessui-state=open]>div:first-child>button]:ring-light-6"
              selectedItem={'전체'}
              onSelected={() => {}}
            ></Dropdown>
          </div>
          {/* 자원명 */}
          <div className="ml-[7px]">
            <p
              data-testid="operation-settlement-page-prediction-settlement-resource-name-title"
              className="noto-11-bold mt-[17px] leading-[22px] text-[#7A828A]"
            >
              {t('settlement.resourceName')}
            </p>
            <Dropdown
              testId="operation-settlement-page-prediction-settlement-resource-name-dropdown"
              items={['전체']}
              className="mt-[2px] w-[180px] [&>div:first-child>button]:noto-13-medium [&>div:last-child>div>p]:pop-12-normal [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[36px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:px-[10px] [&>div:first-child>button]:pb-[7px] [&>div:first-child>button]:pt-[6px] [&>div:first-child>button]:leading-[23px] [&>div:first-child>button]:ring-1 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-light-6 [&>div:last-child>div>p]:px-[10px] [&>div:last-child>div]:py-[13px] [&[data-headlessui-state=open]>div:first-child>button]:ring-light-6"
              selectedItem={'전체'}
              onSelected={() => {}}
            ></Dropdown>
          </div>
          {/* 조회 기간 */}
          <div className="ml-[27px]">
            <p
              data-testid="operation-settlement-page-prediction-settlement-search-period-title"
              className="noto-11-bold mt-[17px] leading-[22px] text-[#7A828A]"
            >
              {t('settlement.searchPeriod')}
            </p>
            <div className="flex items-center">
              <Dropdown
                testId="operation-settlement-page-prediction-settlement-search-period-year"
                items={years}
                className="mt-[2px] w-[120px] [&>div:first-child>button]:noto-13-medium [&>div:last-child>div>p]:pop-12-normal [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[36px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:px-[10px] [&>div:first-child>button]:pb-[7px] [&>div:first-child>button]:pt-[6px] [&>div:first-child>button]:leading-[23px] [&>div:first-child>button]:ring-1 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-light-6 [&>div:last-child>div>p]:px-[10px] [&>div:last-child>div]:py-[13px] [&[data-headlessui-state=open]>div:first-child>button]:ring-light-6"
                selectedItem={selectedYear}
                onSelected={() => {}}
              ></Dropdown>
              <Dropdown
                testId="operation-settlement-page-prediction-settlement-search-period-month"
                items={months}
                className="ml-[7px] mt-[2px] w-[100px] [&>div:first-child>button]:noto-13-medium [&>div:last-child>div>p]:pop-12-normal [&>div:first-child>button:focus]:outline-none [&>div:first-child>button]:h-[36px] [&>div:first-child>button]:bg-white [&>div:first-child>button]:px-[10px] [&>div:first-child>button]:pb-[7px] [&>div:first-child>button]:pt-[6px] [&>div:first-child>button]:leading-[23px] [&>div:first-child>button]:ring-1 [&>div:first-child>button]:ring-inset [&>div:first-child>button]:ring-light-6 [&>div:last-child>div>p]:px-[10px] [&>div:last-child>div]:py-[13px] [&[data-headlessui-state=open]>div:first-child>button]:ring-light-6"
                selectedItem={selectedMonth}
                onSelected={() => {}}
              ></Dropdown>
              <SearchButton
                className="ml-[14px] mt-[2px]"
                testId="operation-settlement-page-prediction-settlement-search-button"
              />
            </div>
          </div>
        </div>
        {/* 총 예측자원 정산금 */}
        <OperationSettlementSummary
          className="mt-[30px]"
          testId="operation-settlement-page-prediction-settlement-summary"
        />
        {/* 일괄 정산 완료 */}
        <div className="mt-[30px] flex items-center">
          <OperationSettlementBatchButton
            className="w-[110px]"
            isClicked={false}
            testId="operation-settlement-page-prediction-settlement-batch-complete-button"
            text={t('settlement.batchCompleteButton')}
            onClick={() =>
              setShowButtonModal({
                isActive: true,
                title: t('settlement.batchSettlementModalTitle'),
                description: t('settlement.batchSettlementModalDescription'),
              })
            }
          />
          <OperationSettlementBatchButton
            className="ml-[10px] w-[152px]"
            isClicked={false}
            testId="operation-settlement-page-prediction-settlement-batch-invoice-issue-button"
            text={t('settlement.batchInvoiceIssueButton')}
            onClick={() =>
              setShowButtonModal({
                isActive: true,
                title: t('settlement.batchInvoiceModalTitle'),
                description: t('settlement.batchInvoiceModalDescription'),
              })
            }
          />
          {showButtonModal.isActive &&
            operationSettlementPageRef &&
            createPortal(
              <div className="z-100 absolute left-0 top-0 flex h-full w-full items-center justify-center bg-black bg-opacity-80">
                <OperationSettlementButtonModal
                  testId="operation-settlement-page-prediction-settlement-batch-button-modal"
                  onClick={() =>
                    setShowButtonModal((prev) => ({ ...prev, isActive: false }))
                  }
                  className="h-[180px] w-[370px]"
                  title={showButtonModal.title}
                  description={showButtonModal.description}
                />
              </div>,

              operationSettlementPageRef
            )}
          <p
            className="noto-11-medium ml-auto text-dark-9"
            data-testid="operation-settlement-page-prediction-settlement-current-status-title"
          >
            {t('settlement.currentStatusTitle')}
          </p>
          <OperationSettlementStatusBar
            className="ml-3"
            testId="operation-settlement-page-prediction-settlement-current-status-bar"
          />
        </div>
        <OperationSettlementTable
          className="mt-[15px]"
          data={testSettlementTableData}
          onClick={(data: { title: string }) =>
            setShowSummaryModal((prev) => ({
              ...prev,
              ...data,
              isActive: true,
            }))
          }
          testId="operation-settlement-page-prediction-settlement-table"
        />
        {showSummaryModal.isActive &&
          operationSettlementPageRef &&
          createPortal(
            <div className="z-100 absolute left-0 top-0 flex h-full w-full items-center justify-center bg-black bg-opacity-80">
              <OperationSettlementSummaryModal
                testId="operation-settlement-page-prediction-settlement-summary-modal"
                onClick={() =>
                  setShowSummaryModal((prev) => ({ ...prev, isActive: false }))
                }
                className="w-[500px]"
                title={showSummaryModal.title}
              />
            </div>,

            operationSettlementPageRef
          )}
      </section>
    </SideLayout>
  );
};
